import React, { useState, forwardRef, SelectHTMLAttributes } from "react";
import { cn } from "@/utils/cn";

// Interface atualizada para aceitar props de Select HTML e forwardRef
interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value'> {
  id: string;
  label: string;
  options: { value: string; label: string }[];
  value?: string; // RHF passará o valor aqui
  // onChange é passado via {...props}
  required?: boolean;
  error?: boolean;
  className?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>((
  {
    id,
    label,
    options,
    value: propValue, // Valor vindo de RHF
    required = false,
    error = false,
    className,
    ...props // Inclui onChange, onBlur, name de RHF
  },
  ref
) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };
  const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(false);
    props.onBlur?.(e);
  };

  // Usar propValue para determinar se o label deve flutuar
  const shouldFloat = isFocused || (propValue && propValue !== "");

  const baseSelectClasses = "peer w-full pl-3 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white appearance-none transition-colors duration-200 ease-in-out";
  const normalSelectClasses = "border-gray-300 focus:border-primary focus:ring-primary";
  const errorSelectClasses = "border-red-500 focus:border-red-500 focus:ring-red-500";

  const baseLabelClasses = "absolute left-3 bg-white px-1 text-sm transition-all duration-200 ease-in-out pointer-events-none";
  const normalLabelClasses = "text-gray-400 peer-focus:text-primary";
  const errorLabelClasses = "text-red-600 peer-focus:text-red-600";

  const floatingLabelTransform = "-top-3 text-xs";
  const restingLabelTransform = "top-2.5 text-base";

  return (
    <div className={cn("relative w-full", className)}>
      <select
        ref={ref}
        id={id}
        required={required}
        // Definir o valor explicitamente
        value={propValue || ''}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(
          baseSelectClasses,
          error ? errorSelectClasses : normalSelectClasses
        )}
        {...props} // Passa onChange, name, onBlur etc.
      >
        {/* Manter a option de placeholder se não for obrigatório ou se nenhum valor for selecionado */}
        {(!required || !propValue) && (
            <option value="" disabled hidden={required}>
                {/* Pode adicionar um texto como "Selecione..." aqui se desejar */} 
            </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {/* Ícone de dropdown */}
       <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
         <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
           <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/>
         </svg>
       </div>
      <label
        htmlFor={id}
        className={cn(
          baseLabelClasses,
          error ? errorLabelClasses : normalLabelClasses,
          // Flutua se estiver focado ou se tiver um valor selecionado (diferente de string vazia)
          shouldFloat ? floatingLabelTransform : restingLabelTransform
        )}
      >
        {label}
      </label>
    </div>
  );
});

Select.displayName = "Select";
